/**
 * Native American Flute Multi-Player
 * Healing Sounds
 */
class NafPlayer {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('.hs-naf-item');
    this.toggleBtns = container.querySelectorAll('.hs-naf-toggle__btn');
    this.stopBtn = container.querySelector('.hs-naf-stop__btn');
    
    this.currentAudio = null;
    this.currentItem = null;
    this.currentEffect = 'dry'; // 'dry' or 'reverb'
    
    this.init();
  }

  init() {
    // Toggle buttons
    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => this.setEffect(btn.dataset.effect));
    });

    // Play buttons
    this.items.forEach(item => {
      const playBtn = item.querySelector('.hs-naf-item__play');
      playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePlay(item);
      });
      
      // Click anywhere on item also plays
      item.addEventListener('click', () => this.togglePlay(item));
    });

    // Stop button
    if (this.stopBtn) {
      this.stopBtn.addEventListener('click', () => this.stopAll());
    }

    // Pause icon SVG
    this.pauseSvg = '<svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
    this.playSvg = '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';
  }

  setEffect(effect) {
    this.currentEffect = effect;
    
    // Update toggle buttons
    this.toggleBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.effect === effect);
    });

    // If currently playing, switch audio source
    if (this.currentAudio && this.currentItem) {
      const currentTime = this.currentAudio.currentTime;
      const wasPlaying = !this.currentAudio.paused;
      
      this.currentAudio.pause();
      
      const newSrc = this.getAudioSrc(this.currentItem);
      this.currentAudio.src = newSrc;
      this.currentAudio.currentTime = Math.min(currentTime, this.currentAudio.duration || currentTime);
      
      if (wasPlaying) {
        this.currentAudio.play().catch(() => {});
      }
    }
  }

  getAudioSrc(item) {
    return this.currentEffect === 'reverb' 
      ? item.dataset.srcReverb 
      : item.dataset.srcDry;
  }

  togglePlay(item) {
    // If clicking same item that's playing, pause it
    if (this.currentItem === item && this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
      return;
    }

    // Stop any current playback
    this.stopAll();

    // Create new audio
    const src = this.getAudioSrc(item);
    if (!src) {
      console.error('No audio source for', item.dataset.note);
      return;
    }

    this.currentItem = item;
    this.currentAudio = new Audio(src);
    
    item.classList.add('loading');

    // Event: can play
    this.currentAudio.addEventListener('canplay', () => {
      item.classList.remove('loading');
    }, { once: true });

    // Event: playing
    this.currentAudio.addEventListener('play', () => {
      item.classList.add('playing');
      item.querySelector('.hs-naf-item__play').innerHTML = this.pauseSvg;
    });

    // Event: pause
    this.currentAudio.addEventListener('pause', () => {
      item.classList.remove('playing');
      item.querySelector('.hs-naf-item__play').innerHTML = this.playSvg;
    });

    // Event: ended
    this.currentAudio.addEventListener('ended', () => {
      this.resetItem(item);
      this.currentItem = null;
      this.currentAudio = null;
    });

    // Event: time update
    this.currentAudio.addEventListener('timeupdate', () => {
      this.updateProgress(item);
    });

    // Event: error
    this.currentAudio.addEventListener('error', () => {
      item.classList.remove('loading', 'playing');
      item.classList.add('error');
      console.error('Error loading audio:', src);
    });

    // Play
    this.currentAudio.play().catch(err => {
      console.error('Playback error:', err);
      item.classList.remove('loading');
    });
  }

  updateProgress(item) {
    if (!this.currentAudio) return;

    const progressBar = item.querySelector('.hs-naf-item__progress-bar');
    const timeDisplay = item.querySelector('.hs-naf-item__time');

    const current = this.currentAudio.currentTime;
    const duration = this.currentAudio.duration || 0;
    const percent = duration > 0 ? (current / duration) * 100 : 0;

    progressBar.style.width = percent + '%';
    timeDisplay.textContent = this.formatTime(current);
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  resetItem(item) {
    item.classList.remove('playing', 'loading');
    item.querySelector('.hs-naf-item__play').innerHTML = this.playSvg;
    item.querySelector('.hs-naf-item__progress-bar').style.width = '0%';
    item.querySelector('.hs-naf-item__time').textContent = '0:00';
  }

  stopAll() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    if (this.currentItem) {
      this.resetItem(this.currentItem);
      this.currentItem = null;
    }

    // Reset all items just in case
    this.items.forEach(item => {
      item.classList.remove('playing', 'loading');
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.hs-naf-player').forEach(container => {
    new NafPlayer(container);
  });
});

// Re-init on Shopify section load (Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('.hs-naf-player');
  if (container) {
    new NafPlayer(container);
  }
});

